import React, { useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import { Check as CheckIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { Alert, Autocomplete, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { useGetMonthlyVendorSummary } from '~/hooks/apis/vendors/useGetMonthlyVendorSummary';
import { useGetVendorPromotions } from '~/hooks/apis/vendors/useGetVendorPromotions';
import { useVendorSearch } from '~/hooks/apis/vendors/useVendorSearch';
import useDebounced from '~/hooks/useDebounced';

import type { Dayjs } from '~/services/TimeService';

import { buildMonthlyPerformanceEmail } from './buildEmail';

const MONTH_FORMAT = 'YYYY-MM';

function defaultMonth(): Dayjs {
  return dayjs().subtract(1, 'month').startOf('month');
}

export default function MonthlyCampaignPerformancePage() {
  const [vendor, setVendor] = useState<App.Vendor | null>(null);
  const [vendorInput, setVendorInput] = useState('');
  const debouncedVendorInput = useDebounced(vendorInput, 300);

  const [month, setMonth] = useState<Dayjs | null>(defaultMonth());
  const monthString = month?.isValid() ? month.format(MONTH_FORMAT) : '';

  const [copied, setCopied] = useState(false);

  const vendorSearch = useVendorSearch({
    filter: debouncedVendorInput,
    enabled: debouncedVendorInput.length >= 2 && debouncedVendorInput !== vendor?.name,
  });

  const summaryQuery = useGetMonthlyVendorSummary({
    vendorId: vendor?.sfid ?? null,
    month: monthString,
  });

  const promotionsWindow = useMemo(() => {
    if (!month?.isValid()) return { from: '', to: '' };
    return {
      from: month.startOf('month').format('YYYY-MM-DD'),
      to: month.endOf('month').format('YYYY-MM-DD'),
    };
  }, [month]);

  const promotionsQuery = useGetVendorPromotions({
    vendorId: vendor?.sfid ?? null,
    from: promotionsWindow.from,
    to: promotionsWindow.to,
    enabled: !!promotionsWindow.from,
  });

  const isLoadingEmail = !!vendor && (summaryQuery.isPending || promotionsQuery.isPending);

  const generatedEmail = useMemo(() => {
    if (!summaryQuery.data || !vendor || promotionsQuery.isPending) {
      return '';
    }
    return buildMonthlyPerformanceEmail({
      summary: summaryQuery.data,
      vendorName: vendor.name,
      promotions: promotionsQuery.data?.rows ?? [],
    });
  }, [summaryQuery.data, vendor, promotionsQuery.data, promotionsQuery.isPending]);

  const handleCopy = async () => {
    if (!generatedEmail) {
      return;
    }
    try {
      await navigator.clipboard.writeText(generatedEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available in all contexts
    }
  };

  const vendorOptions = vendorSearch.data?.result ?? [];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Monthly Performance — Email Generator
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Generate a monthly performance email for a Tier 1 partner. Pick a vendor and reporting month, then copy the
        generated email into your email client.
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={3} alignItems="flex-start">
        <Autocomplete<App.Vendor>
          sx={{ flex: 1, minWidth: 300 }}
          value={vendor}
          onChange={(_, value) => setVendor(value)}
          inputValue={vendorInput}
          onInputChange={(_, value) => setVendorInput(value)}
          options={vendorOptions}
          getOptionLabel={(option) => `${option.name} (${option.sfid})`}
          isOptionEqualToValue={(option, val) => option.sfid === val.sfid}
          loading={vendorSearch.isFetching}
          filterOptions={(options) => options}
          noOptionsText={debouncedVendorInput.length < 2 ? 'Type at least 2 characters' : 'No vendors found'}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vendor"
              placeholder="Search vendor by name or ID"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {vendorSearch.isFetching ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <DatePicker
          label="Reporting month"
          views={['year', 'month']}
          openTo="month"
          format="MMMM YYYY"
          value={month}
          onChange={(value) => setMonth(value)}
          maxDate={dayjs().startOf('month')}
          slotProps={{ textField: { sx: { minWidth: 220 } } }}
        />
      </Stack>

      {summaryQuery.isError && (
        <Box mt={3}>
          <Alert severity="error">
            Failed to load summary: {summaryQuery.error instanceof Error ? summaryQuery.error.message : 'Unknown error'}
          </Alert>
        </Box>
      )}

      {promotionsQuery.isError && (
        <Box mt={3}>
          <Alert severity="error">
            Failed to load promotions:{' '}
            {promotionsQuery.error instanceof Error ? promotionsQuery.error.message : 'Unknown error'}
          </Alert>
        </Box>
      )}

      {isLoadingEmail && (
        <Box mt={3} display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading…</Typography>
        </Box>
      )}

      {!vendor && (
        <Box mt={3}>
          <Alert severity="info">Select a vendor to generate the monthly performance email.</Alert>
        </Box>
      )}

      {generatedEmail && (
        <Box mt={3}>
          <Stack direction="row" spacing={2} mb={2} alignItems="center">
            <Button
              variant="contained"
              color="primary"
              startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
              onClick={handleCopy}
            >
              {copied ? 'Copied!' : 'Copy email'}
            </Button>
          </Stack>

          <TextField
            multiline
            minRows={20}
            maxRows={40}
            value={generatedEmail}
            fullWidth
            InputProps={{ readOnly: true }}
          />
        </Box>
      )}
    </Box>
  );
}
